/**
 * The case's company, open in the Case IDE: the petitioner from the company
 * record and the parts this case uses (entity, job, worksite, billing IDs).
 */
import { ArrowUpRight, Building2, Lock } from "lucide-react";
import { useEffect, useState, type FC, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle
} from "@/components/ui/empty";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

import { timeActions } from "../../timeTracking/actions";
import { companyActions, playbookActions } from "../actions";
import { caseFeeText } from "../billingModel";
import { CompanyMark } from "../components/CompanyMark";
import { ruleLabel } from "../playbookModel";
import {
  refreshCompanies,
  useCaseCompany,
  usePlaybookPreview
} from "../queries";
import { companyPath } from "../routes";
import { CaseHrSection } from "./CaseHrSection";
import type {
  CaseCompanyContext,
  CaseLinkPatch,
  Company,
  MatterIds
} from "../types";
import { formatAddress } from "../utils";

const NONE = "__none__";

const Section: FC<{
  title: string;
  aside?: ReactNode;
  children: ReactNode;
}> = ({ title, aside, children }) => (
  <div className="flex flex-col gap-2 border-b border-line-2 px-4 py-3">
    <span className="flex items-center gap-2">
      <span className="text-sm font-medium text-fg-3">{title}</span>
      <span className="flex-1" />
      {aside}
    </span>
    {children}
  </div>
);

const KV: FC<{ k: string; children: ReactNode; mono?: boolean }> = ({
  k,
  children,
  mono
}) => (
  <div className="grid min-h-7 grid-cols-[110px_minmax(0,1fr)] items-center gap-2 text-base">
    <span className="text-fg-3">{k}</span>
    <span className={mono ? "font-mono text-sm" : "min-w-0"}>{children}</span>
  </div>
);

const IdField: FC<{
  label: string;
  value: string | null;
  onSave: (v: string) => void;
}> = ({ label, value, onSave }) => {
  const [text, setText] = useState(value ?? "");
  useEffect(() => setText(value ?? ""), [value]);
  return (
    <Input
      aria-label={label}
      value={text}
      placeholder="–"
      className="h-7 px-2 font-mono text-sm"
      onChange={(e) => setText(e.currentTarget.value)}
      onBlur={() => text.trim() !== (value ?? "") && onSave(text.trim())}
      onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
    />
  );
};

const PartSelect: FC<{
  label: string;
  value: string | null;
  options: Array<{ id: string; label: string }>;
  empty?: string;
  onChange: (id: string | null) => void;
}> = ({ label, value, options, empty, onChange }) => (
  <Select
    value={value ?? NONE}
    onValueChange={(v) => onChange(v === NONE ? null : v)}
  >
    <SelectTrigger size="sm" aria-label={label} className="h-7 w-full text-sm">
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      {empty ? <SelectItem value={NONE}>{empty}</SelectItem> : null}
      {options.map((o) => (
        <SelectItem key={o.id} value={o.id}>
          {o.label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const CasePlaybook: FC<{ company: Company; individualId: string }> = ({
  company,
  individualId
}) => {
  const preview = usePlaybookPreview(company.id, individualId);
  const byId = new Map((company.playbook ?? []).map((r) => [r.id, r]));
  const rows = (preview.data?.rules ?? []).flatMap((r) => {
    const rule = byId.get(r.id);
    return rule ? [{ rule, on: r.on }] : [];
  });
  const on = rows.filter((r) => r.on).length;
  return (
    <Section
      title={
        preview.isLoading
          ? "Playbook on this case"
          : `Playbook on this case · ${on} of ${rows.length}`
      }
      aside={
        <Button asChild size="xs" variant="ghost">
          <Link to={companyPath(company.id, "playbook")}>Edit rules</Link>
        </Button>
      }
    >
      {preview.isLoading ? (
        <Skeleton className="h-12" />
      ) : rows.length ? (
        rows.map(({ rule, on: isOn }) => (
          <span
            key={rule.id}
            className="flex items-start gap-2.5"
            data-testid="case-playbook-rule"
          >
            <span
              className="w-12 shrink-0 pt-0.5 font-mono text-sm text-fg-3"
              title={ruleLabel(rule) ?? undefined}
            >
              {rule.number ? `Rule ${rule.number}` : null}
            </span>
            <span
              className={cn(
                "flex-1 text-sm leading-4.5",
                !isOn && "text-fg-3 line-through"
              )}
            >
              {rule.text}
            </span>
            <Switch
              aria-label={`${isOn ? "Turn off" : "Turn on"} ${ruleLabel(
                rule
              )} on this case`}
              checked={isOn}
              onCheckedChange={(next) =>
                void playbookActions.setOverride(
                  company,
                  individualId,
                  rule,
                  next
                )
              }
            />
          </span>
        ))
      ) : (
        <span className="text-sm text-fg-3">
          No {company.name} rules apply to this case type.
        </span>
      )}
    </Section>
  );
};

const CaseFeeSection: FC<{
  company: Company;
  individualId: string;
  fee: NonNullable<CaseCompanyContext["fee"]>;
  lock: string | null;
}> = ({ company, individualId, fee, lock }) => {
  const employee = 100 - fee.employer_share;
  const followSchedule = async () => {
    const saved = await timeActions.saveCaseBilling(individualId, {
      company_terms: true
    });
    if (saved) toast(`Back on ${company.name}'s fee schedule`);
    await refreshCompanies();
  };
  return (
    <Section
      title="Fee and who pays"
      aside={
        <span className="text-sm text-fg-3">
          {fee.company_terms
            ? `${company.name}'s fee schedule`
            : "Set on this case"}
        </span>
      }
    >
      <KV k="Fee">
        <span className="tabular-nums" data-testid="case-pane-fee">
          {caseFeeText(fee)}
        </span>
      </KV>
      <KV k="Who pays">
        <span className="flex flex-wrap items-center gap-1.5">
          {company.name} {fee.employer_share}%
          {employee > 0 ? (
            <span className="text-fg-3">
              · employee {employee}%, exported under them
            </span>
          ) : null}
        </span>
      </KV>
      {lock ? (
        <span
          className="flex items-start gap-1.5 text-sm text-fg-3"
          data-testid="case-pane-fee-lock"
        >
          <Lock className="mt-0.5 size-3 shrink-0" />
          {lock}
        </span>
      ) : null}
      {fee.company_terms ? null : (
        <Button
          size="xs"
          variant="secondary"
          className="w-fit"
          onClick={() => void followSchedule()}
        >
          Use {company.name}&apos;s terms
        </Button>
      )}
    </Section>
  );
};

export const CaseCompanyPane: FC<{ individualId: string }> = ({
  individualId
}) => {
  const { data, isLoading } = useCaseCompany(individualId);
  if (isLoading) return <Skeleton className="m-4 h-40" />;
  const company = data?.company;
  const link = data?.link;
  if (!data || !company || !link) {
    return (
      <div className="p-4">
        <Empty className="border border-dashed border-line-2">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Building2 />
            </EmptyMedia>
            <EmptyTitle>Not linked to a company</EmptyTitle>
            <EmptyDescription>
              Link the case from the company page or the case&apos;s employer
              settings.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }
  const { matter } = data;
  const { entity, job_description: job, worksite } = link;
  const current: CaseLinkPatch = {
    entity_id: entity?.id ?? null,
    job_description_id: job?.id ?? null,
    worksite_id: worksite?.id ?? null
  };
  const setPart = (
    key: "entity_id" | "job_description_id" | "worksite_id",
    id: string | null
  ) =>
    void companyActions.updateCase(
      company,
      individualId,
      { [key]: id },
      { [key]: current[key] }
    );
  const saveId = (field: keyof MatterIds) => (value: string) =>
    void companyActions.saveMatterId(
      company,
      individualId,
      field,
      value,
      matter[field]
    );

  return (
    <div
      className="flex h-full min-h-0 flex-col overflow-y-auto bg-surface"
      data-testid="case-company-pane"
    >
      <div className="flex items-center gap-2.5 border-b border-line-2 px-4 py-3">
        <CompanyMark name={company.name} className="size-7 text-sm" />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-base font-medium">
            From {company.name}
          </span>
          <span className="truncate text-sm text-fg-3">
            Company details and rules this case uses
          </span>
        </span>
        <Button asChild size="xs" variant="ghost">
          <Link to={companyPath(company.id)}>
            <ArrowUpRight /> Open
          </Link>
        </Button>
      </div>

      <Section
        title="Petitioner"
        aside={
          <span className="text-sm text-fg-3">
            Company record · edits apply to {company.case_count}{" "}
            {company.case_count === 1 ? "case" : "cases"}
          </span>
        }
      >
        <KV k="Entity">
          {company.entities.length > 1 ? (
            <PartSelect
              label="Entity"
              value={entity?.id ?? null}
              options={company.entities.map((e) => ({
                id: e.id,
                label: e.legal_name
              }))}
              onChange={(id) => setPart("entity_id", id)}
            />
          ) : (
            entity?.legal_name
          )}
        </KV>
        <KV k="FEIN" mono>
          {entity?.fein || "–"}
        </KV>
        <KV k="Signatory">
          {entity?.signatory?.name
            ? `${entity.signatory.name}${
                entity.signatory.title ? `, ${entity.signatory.title}` : ""
              }`
            : "–"}
        </KV>
        <KV k="Address">{formatAddress(entity?.address) || "–"}</KV>
      </Section>

      <Section title="This case">
        <KV k="Job">
          {company.job_descriptions.length ? (
            <PartSelect
              label="Job description"
              value={job?.id ?? null}
              empty={(!job && link.job_title) || "No job yet"}
              options={company.job_descriptions.map((j) => ({
                id: j.id,
                label: j.soc_code ? `${j.title} · SOC ${j.soc_code}` : j.title
              }))}
              onChange={(id) => setPart("job_description_id", id)}
            />
          ) : (
            link.job_title || "–"
          )}
        </KV>
        <KV k="Worksite">
          {company.worksites.length ? (
            <PartSelect
              label="Worksite"
              value={worksite?.id ?? null}
              empty="Not set"
              options={company.worksites.map((w) => ({
                id: w.id,
                label: w.remote ? `${w.name || "Remote"} (remote)` : w.name
              }))}
              onChange={(id) => setPart("worksite_id", id)}
            />
          ) : (
            "–"
          )}
        </KV>
        <KV k="Client matter ID">
          <IdField
            label="Client matter ID"
            value={matter.client_matter_id}
            onSave={saveId("client_matter_id")}
          />
        </KV>
        <KV k="PO">
          <IdField label="PO" value={matter.po} onSave={saveId("po")} />
        </KV>
        <KV k="Cost center">
          <IdField
            label="Cost center"
            value={matter.cost_center}
            onSave={saveId("cost_center")}
          />
        </KV>
      </Section>

      {data.fee ? (
        <CaseFeeSection
          company={company}
          individualId={individualId}
          fee={data.fee}
          lock={data.fee_lock ?? null}
        />
      ) : null}

      <CaseHrSection company={company} individualId={individualId} />

      <CasePlaybook company={company} individualId={individualId} />
    </div>
  );
};
